import React from 'react'
import { X, User, Mail, Phone, Shield, Building, Clock, Calendar, Activity, Key, AlertTriangle } from 'lucide-react'
import { format, formatDistanceToNow } from 'date-fns'

interface AdminDetailsModalProps {
  admin: {
    id: string
    email: string
    firstName: string
    lastName: string
    role: string
    status: string
    lastLogin: Date | null
    createdAt: Date
    updatedAt: Date
    department?: string
    phoneNumber?: string
    twoFactorEnabled: boolean
    loginAttempts: number
  }
  onClose: () => void
}

const AdminDetailsModal: React.FC<AdminDetailsModalProps> = ({ admin, onClose }) => {
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-500/10 text-purple-500'
      case 'support_admin':
        return 'bg-blue-500/10 text-blue-500'
      case 'content_admin':
        return 'bg-green-500/10 text-green-500'
      case 'finance_admin':
        return 'bg-orange-500/10 text-orange-500'
      default:
        return 'bg-gray-500/10 text-gray-500'
    }
  }

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-500'
      case 'inactive':
        return 'bg-gray-500/10 text-gray-500'
      case 'locked':
        return 'bg-red-500/10 text-red-500'
      default:
        return 'bg-gray-500/10 text-gray-500'
    }
  }

  // Mock audit logs - will be replaced with API data
  const auditLogs = [
    {
      id: '1',
      action: 'Login',
      timestamp: new Date('2025-01-20T10:30:00'),
      ipAddress: '192.168.1.1',
      userAgent: 'Chrome/120.0.0.0',
    },
    {
      id: '2',
      action: 'Updated user profile',
      timestamp: new Date('2025-01-19T14:20:00'),
      ipAddress: '192.168.1.1',
      userAgent: 'Chrome/120.0.0.0',
    },
    {
      id: '3',
      action: 'Changed password',
      timestamp: new Date('2025-01-18T09:15:00'),
      ipAddress: '192.168.1.2',
      userAgent: 'Safari/17.0',
    },
    {
      id: '4',
      action: 'Enabled 2FA',
      timestamp: new Date('2025-01-17T16:45:00'),
      ipAddress: '192.168.1.1',
      userAgent: 'Chrome/120.0.0.0',
    },
  ]

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-lg font-medium text-primary">
                {admin.firstName[0]}{admin.lastName[0]}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {admin.firstName} {admin.lastName}
              </h2>
              <p className="text-sm text-muted-foreground">{admin.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Status and Role */}
          <div className="flex items-center space-x-4 mb-6">
            <span className={`px-3 py-1 text-sm rounded-full ${getStatusBadgeColor(admin.status)}`}>
              {admin.status}
            </span>
            <span className={`px-3 py-1 text-sm rounded-full ${getRoleBadgeColor(admin.role)}`}>
              {admin.role.replace('_', ' ')}
            </span>
            {admin.twoFactorEnabled && (
              <span className="px-3 py-1 text-sm rounded-full bg-green-500/10 text-green-500 flex items-center space-x-1">
                <Shield className="w-3 h-3" />
                <span>2FA Enabled</span>
              </span>
            )}
            {admin.loginAttempts >= 3 && (
              <span className="px-3 py-1 text-sm rounded-full bg-yellow-500/10 text-yellow-500 flex items-center space-x-1">
                <AlertTriangle className="w-3 h-3" />
                <span>{admin.loginAttempts} Failed Attempts</span>
              </span>
            )}
          </div>

          {/* Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Personal Information */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Personal Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Full Name</p>
                    <p className="text-sm text-foreground">{admin.firstName} {admin.lastName}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="text-sm text-foreground">{admin.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Phone</p>
                    <p className="text-sm text-foreground">{admin.phoneNumber || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Building className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Department</p>
                    <p className="text-sm text-foreground">{admin.department || 'Not assigned'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Account Information</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p className="text-sm text-foreground">
                      {format(admin.createdAt, 'MMM dd, yyyy')} ({formatDistanceToNow(admin.createdAt, { addSuffix: true })})
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Last Login</p>
                    <p className="text-sm text-foreground">
                      {admin.lastLogin 
                        ? `${format(admin.lastLogin, 'MMM dd, yyyy HH:mm')} (${formatDistanceToNow(admin.lastLogin, { addSuffix: true })})`
                        : 'Never logged in'
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Activity className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Last Updated</p>
                    <p className="text-sm text-foreground">
                      {format(admin.updatedAt, 'MMM dd, yyyy')} ({formatDistanceToNow(admin.updatedAt, { addSuffix: true })})
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Key className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Login Attempts</p>
                    <p className="text-sm text-foreground">{admin.loginAttempts} failed attempts</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div className="bg-muted/30 rounded-lg p-4 mb-8">
            <h3 className="text-sm font-medium text-foreground mb-3">Permissions</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {admin.role === 'super_admin' && (
                <>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-foreground">Full System Access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-foreground">User Management</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-foreground">Billing Access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-foreground">System Settings</span>
                  </div>
                </>
              )}
              {admin.role === 'support_admin' && (
                <>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-foreground">View Users</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-foreground">Support Tickets</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-foreground">Billing Access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-sm text-foreground">System Settings</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">Recent Activity</h3>
            <div className="space-y-2">
              {auditLogs.map((log) => (
                <div key={log.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center space-x-3">
                    <Activity className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-foreground">{log.action}</p>
                      <p className="text-xs text-muted-foreground">
                        {log.ipAddress} • {log.userAgent}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(log.timestamp, { addSuffix: true })}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-background border border-border rounded-lg hover:bg-muted transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdminDetailsModal